import {Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {ThreadPreview} from './ThreadPreview'
import {ThreadPreviewSkeleton} from '../ThreadPreviewSkeleton'
import {useCompositeThreadPositioning} from '../../../hooks/use-composite-thread-positioning'
import {useDiffEntries} from '../../../stores/selectors'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {CommentsZeroState} from './CommentsZeroState'
import {useSelectedCommentReplyThreadId} from '../../../stores/layout-selectors'

export interface ThreadPreviewsContentProps {
  filteredThreadIds: Set<string>
  selectedReviewId: number | null
  onClose: () => void
  isOpen: boolean
  tabSize?: number
  threadPreviews: Thread[]
}

function ThreadPositioningWithStore({threadPreviews}: {threadPreviews: Thread[]}) {
  useCompositeThreadPositioning(threadPreviews, useDiffEntries())
  return null
}

function ThreadPositioningServerOnly({threadPreviews}: {threadPreviews: Thread[]}) {
  useCompositeThreadPositioning(threadPreviews)
  return null
}

/**
 * Container that reads reply-detail state and renders either the thread list or detail.
 * It also hydrates thread positioning for comments that fall outside the current diff.
 */
export function ThreadPreviewsContent({
  filteredThreadIds,
  selectedReviewId,
  onClose,
  isOpen,
  tabSize,
  threadPreviews,
}: ThreadPreviewsContentProps) {
  return (
    <>
      {isFeatureEnabled('updated_pull_request_header') ? (
        <ThreadPositioningServerOnly threadPreviews={threadPreviews} />
      ) : (
        <ThreadPositioningWithStore threadPreviews={threadPreviews} />
      )}
      <ThreadPreviewsComponent
        threadPreviews={threadPreviews}
        filteredThreadIds={filteredThreadIds}
        selectedReviewId={selectedReviewId}
        onClose={onClose}
        isOpen={isOpen}
        tabSize={tabSize}
      />
    </>
  )
}

interface ThreadPreviewsComponentProps {
  threadPreviews: Thread[]
  filteredThreadIds: Set<string>
  selectedReviewId: number | null
  onClose: () => void
  isOpen: boolean
  tabSize?: number
}

/**
 * Presentational thread previews renderer. It announces result counts, shows zero state
 * for empty filtered results, and switches to a single selected thread in detail mode.
 */
function ThreadPreviewsComponent({
  threadPreviews,
  filteredThreadIds,
  selectedReviewId,
  onClose,
  isOpen,
  tabSize,
}: ThreadPreviewsComponentProps) {
  const selectedThreadId = useSelectedCommentReplyThreadId()
  const announceRef = useRef<HTMLDivElement>(null)
  const previousSelectedThreadIdRef = useRef(selectedThreadId)
  const capturedScrollTopRef = useRef<number | null>(null)
  const capturedScrollContainerRef = useRef<HTMLElement | null>(null)
  const threadListRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @eslint-react/no-unnecessary-use-memo -- need a stable reference to the debounce method
  const debouncedAnnounce = useMemo(() => debounce(announce, 300), [])
  const hasThreads = threadPreviews.length > 0
  const selectedThread = useMemo(
    () => threadPreviews.find(thread => thread.id === selectedThreadId) ?? null,
    [threadPreviews, selectedThreadId],
  )

  const threads = useMemo(
    () => threadPreviews.filter(thread => filteredThreadIds.has(thread?.id)),
    [filteredThreadIds, threadPreviews],
  )

  const captureScrollBeforeOpenReplyDetail = useCallback((triggerElement: HTMLElement | null) => {
    const anchor = triggerElement ?? threadListRef.current
    const scrollContainer = findScrollableAncestor(anchor)
    if (!scrollContainer) return

    capturedScrollContainerRef.current = scrollContainer
    capturedScrollTopRef.current = scrollContainer.scrollTop
  }, [])

  useEffect(() => {
    if (!isOpen) return

    const element = announceRef.current as HTMLElement

    const threadCommentsLength = selectedThread?.commentsData?.comments?.length
    if (threadCommentsLength) {
      debouncedAnnounce(`${threadCommentsLength} thread comments`, {element})
      return
    }

    const announceText =
      threads.length > 0 ? `${threads.length} ${threads.length === 1 ? 'comment' : 'comments'}` : `No comments found`
    debouncedAnnounce(announceText, {element})
  }, [isOpen, debouncedAnnounce, threads, selectedThread?.commentsData?.comments?.length])

  useLayoutEffect(() => {
    const wasInThreadDetail = previousSelectedThreadIdRef.current !== undefined
    const isInThreadList = selectedThreadId === undefined

    if (wasInThreadDetail && isInThreadList && capturedScrollTopRef.current !== null) {
      const capturedScrollContainer = capturedScrollContainerRef.current
      const scrollContainer =
        capturedScrollContainer && capturedScrollContainer.isConnected
          ? capturedScrollContainer
          : findScrollableAncestor(threadListRef.current)
      const savedScrollTop = capturedScrollTopRef.current

      if (scrollContainer) {
        scrollContainer.scrollTo({top: savedScrollTop})
      }
      capturedScrollTopRef.current = null
      capturedScrollContainerRef.current = null
    }

    previousSelectedThreadIdRef.current = selectedThreadId
  }, [selectedThreadId])

  return (
    <>
      {threads.length > 0 ? (
        <ErrorBoundary boundaryName="DockedThreadPreviewsContent" fallback={<CommentsErrorFallback />}>
          <Suspense fallback={<ThreadPreviewSkeleton />}>
            {selectedThread ? (
              <div className="d-flex flex-column position-relative width-full gap-3">
                <ThreadPreview
                  tabSize={tabSize}
                  thread={selectedThread}
                  selectedReviewId={null}
                  onNavigateToDiffComment={onClose}
                  headingLevel="h2"
                  onBeforeOpenReplyDetail={captureScrollBeforeOpenReplyDetail}
                />
              </div>
            ) : (
              <div ref={threadListRef} className="d-flex flex-column position-relative width-full gap-3">
                {threads.map(thread => (
                  <ThreadPreview
                    key={thread.id}
                    tabSize={tabSize}
                    thread={thread}
                    selectedReviewId={selectedReviewId}
                    onNavigateToDiffComment={onClose}
                    headingLevel="h2"
                    onBeforeOpenReplyDetail={captureScrollBeforeOpenReplyDetail}
                  />
                ))}
              </div>
            )}
          </Suspense>
        </ErrorBoundary>
      ) : (
        <CommentsZeroState hasItems={hasThreads} />
      )}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={announceRef} />
    </>
  )
}

function findScrollableAncestor(start: HTMLElement | null): HTMLElement | null {
  let element = start
  while (element) {
    if (isScrollableContainer(element)) {
      return element
    }
    element = element.parentElement
  }
  return null
}

function isScrollableContainer(element: HTMLElement): boolean {
  if (element.classList.contains('overflow-auto') || element.classList.contains('overflow-y-auto')) return true

  const className = element.className
  return typeof className === 'string' && className.includes('prc-Dialog-Body')
}
